import { Slot, Slottable } from '@radix-ui/react-slot';
import classNames from 'classnames';
import omit from 'lodash-es/omit';
import React from 'react';
import styles from './Button.module.scss';
import LoadingIcon from './icons/LoadingIcon';
import Tooltip from './Tooltip';

const buttonPriorities = ['xlow', 'low', 'medium', 'high', 'xhigh'] as const;
export type ButtonPriority = (typeof buttonPriorities)[number];
export type ButtonVariant = 'default' | 'danger' | 'success' | 'floating' | 'disabled';
export type ButtonSize = 'xsmall' | 'small' | 'medium' | 'large';

export interface ButtonComponentProps {
  variant?: ButtonVariant;
  priority?: ButtonPriority;
  size?: ButtonSize;
  destructive?: boolean;
  isActive?: boolean;
  disabled?: boolean;
  children?: React.ReactNode;
  tooltip?: React.ReactElement | string;
  selected?: boolean;
  caret?: boolean;
  isLoading?: boolean;
  /**
   * Merges its props onto its immediate child. **MUST ONLY HAVE ONE CHILD**
   * @example <Button asChild><a href="#">...</a></Button> renders <a href="#">...</a> with button styles
   * @link https://www.radix-ui.com/primitives/docs/utilities/slot */
  asChild?: boolean;
}
export interface ButtonProps
  extends ButtonComponentProps,
    React.ButtonHTMLAttributes<HTMLButtonElement> {}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (props, ref): React.ReactElement => {
    const {
      children,
      className,
      isLoading,
      disabled = isLoading,
      type = 'button',
      isActive,
      onClick,
      destructive,
      style,
      tooltip,
      selected,
      caret,
      asChild,
      ...rest
    } = props;

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (disabled) {
        // Links don't have a "disabled" property, so we preventDefault when clicked
        // so it mimicks the behavior of actual buttons.
        e.preventDefault();
        return;
      }
      onClick?.(e);
    };
    const { className: classes } = useButtonClassNames(props);
    const buttonClass = classNames(classes, className);

    const caretMarkup = caret ? <Caret direction={isActive ? 'up' : 'down'} /> : null;

    const loadingMarkup = isLoading ? (
      <div className={styles.loadingMarkup}>
        <LoadingIcon />
      </div>
    ) : null;

    const buttonProps = {
      disabled,
      onClick: handleClick,
      style,
      type,
    };

    const wrappedChildren =
      typeof children === 'string' ? <span className={styles.text}>{children}</span> : children;

    const Component = asChild ? Slot : 'button';

    const button = (
      <Component {...omit(rest, 'priority')} ref={ref} className={buttonClass} {...buttonProps}>
        <Slottable>{wrappedChildren}</Slottable>
        {caretMarkup}
        {loadingMarkup}
      </Component>
    );

    if (tooltip) {
      return (
        <Tooltip position="bottom" usePortal tooltip={tooltip}>
          {button}
        </Tooltip>
      );
    }
    return button;
  }
);

Button.displayName = 'Button';

export default Button;

function validateProps(props: ButtonProps) {
  if (process.env.NODE_ENV === 'production') {
    return;
  }
  const priority = props.priority ?? 'medium';
  if (props.selected && priority !== 'low' && priority !== 'medium') {
    throw new Error(
      `Button: \`selected\` prop is only supported on low & medium priority buttons. Priority: ${priority}`
    );
  }
  if (props.destructive && (priority === 'xlow' || priority === 'xhigh')) {
    throw new Error(
      `Button: \`destructive\` prop is not supported on xlow or xhigh priority buttons. Priority: ${props.priority}`
    );
  }
  if (props.selected && props.destructive) {
    throw new Error('Button: `selected` prop is not supported on destructive buttons.');
  }
}

const Caret = ({
  direction,
  ...props
}: React.SVGProps<SVGSVGElement> & { direction: 'up' | 'down' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" {...props}>
    <path
      d={
        direction === 'up'
          ? 'M6.1018 16.9814C5.02785 16.9814 4.45387 15.7165 5.16108 14.9083L10.6829 8.59762C11.3801 7.80079 12.6197 7.80079 13.3169 8.59762L18.8388 14.9083C19.5459 15.7165 18.972 16.9814 17.898 16.9814H6.1018Z'
          : 'M6.1018 8C5.02785 8 4.45387 9.2649 5.16108 10.0731L10.6829 16.3838C11.3801 17.1806 12.6197 17.1806 13.3169 16.3838L18.8388 10.0731C19.5459 9.2649 18.972 8 17.898 8H6.1018Z'
      }
      fill="currentColor"
    />
  </svg>
);

export function useButtonClassNames(props: ButtonComponentProps) {
  validateProps(props);
  const {
    destructive,
    isActive,
    selected,
    isLoading,
    disabled = isLoading,
    variant = 'default',
    priority = 'medium',
    size = 'medium',
  } = props;
  const activeClassName = styles.isActive;
  const buttonVariant: ButtonVariant = destructive ? 'danger' : variant;

  const className = classNames(
    styles.button,
    styles[`variant-${buttonVariant}`],
    styles[`priority-${buttonPriorities.find(p => p === priority) ?? 'medium'}`],
    styles[`size-${size}`],
    {
      [activeClassName]: isActive,
      [styles.selected]: selected,
      [styles.disabled]: disabled,
      [styles.isLoading]: isLoading,
    }
  );
  return { className, activeClassName };
}
